/**
 * PROPRIETARY NOTICE
 * THE ONESCENT DIAL - Authentication Service
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 * 
 * PURPOSE:
 * Centralized authentication management with:
 * - Clean login/logout/register flows
 * - Token lifecycle management
 * - Session persistence (30 days / 1 day)
 * - Remember email functionality
 * - User state synchronization
 * 
 * ARCHITECTURE:
 * - Uses ApiService for all HTTP requests (prevents body stream errors)
 * - Single source of truth for auth state
 * - Event-based notifications for state changes
 */

import apiService from './ApiService';
import { authLogger } from '../utils/logger';

// Event names for auth state changes
export const AUTH_EVENTS = {
  LOGIN: 'tod_auth_login',
  LOGOUT: 'tod_auth_logout',
  USER_UPDATED: 'tod_auth_user_updated',
  TOKEN_EXPIRED: 'tod_auth_token_expired',
};

/**
 * Authentication Service
 * Manages all authentication-related operations
 */
class AuthService {
  constructor() {
    if (AuthService.instance) {
      return AuthService.instance;
    }
    AuthService.instance = this;
    
    this.user = null;
    this.loading = true;
    this.listeners = new Set();
  }
  
  /**
   * Subscribe to auth state changes
   * @param {Function} callback - Called with { user, loading }
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback);
    // Immediately notify with current state
    callback({ user: this.user, loading: this.loading });
    return () => this.listeners.delete(callback);
  }
  
  /**
   * Notify all subscribers of state change
   */
  notifyListeners() {
    const state = { user: this.user, loading: this.loading };
    this.listeners.forEach(callback => callback(state));
  }
  
  /**
   * Dispatch custom event for global notifications
   */
  dispatchEvent(eventName, detail = {}) {
    // Ensure detail is serializable by creating a plain copy
    const safeDetail = JSON.parse(JSON.stringify(detail));
    window.dispatchEvent(new CustomEvent(eventName, { detail: safeDetail }));
  }
  
  /**
   * Initialize auth state - check for existing session
   * Call this on app startup
   */
  async initialize() {
    this.loading = true;
    this.notifyListeners();
    
    if (!apiService.hasToken()) {
      authLogger.log('No stored token - user not authenticated');
      this.user = null;
      this.loading = false;
      this.notifyListeners();
      return null;
    }
    
    authLogger.log('Found stored token, validating...');
    
    const response = await apiService.get('/auth/me');
    
    if (response.ok) {
      this.user = response.data;
      authLogger.log('Token valid, user:', this.user.name);
      this.dispatchEvent(AUTH_EVENTS.LOGIN, { user: this.user });
    } else if (response.status === 401 || response.status === 403) {
      // Token invalid/expired - clear it
      authLogger.log('Token invalid/expired, clearing');
      apiService.setToken(null);
      this.user = null;
      this.dispatchEvent(AUTH_EVENTS.TOKEN_EXPIRED);
    } else {
      // Other error (network, server) - keep token, might be temporary
      authLogger.warn('Auth check failed:', response.error, '- keeping token');
    }
    
    this.loading = false;
    this.notifyListeners();
    return this.user;
  }
  
  /**
   * Register new user
   * @param {Object} userData - Registration data
   */
  async register(userData) {
    const { email, password, name, natalData, communicationConsent = true, phone = null } = userData;
    
    const response = await apiService.post('/auth/register', {
      email,
      password,
      name,
      natal_data: {
        birth_date: natalData.birth_date,
        birth_time: natalData.birth_time || '12:00',
        birth_latitude: natalData.birth_latitude,
        birth_longitude: natalData.birth_longitude,
        birth_city: natalData.birth_city || null,
      },
      communication_consent: communicationConsent,
      phone,
    });
    
    if (!response.ok) {
      throw new Error(response.error || 'Registration failed');
    }
    
    // Store token from response
    if (response.data.session_token) {
      apiService.setToken(response.data.session_token);
    }
    
    this.user = response.data;
    this.notifyListeners();
    this.dispatchEvent(AUTH_EVENTS.LOGIN, { user: this.user });
    
    return this.user;
  }
  
  /**
   * Login with email/password
   * @param {string} email
   * @param {string} password
   * @param {boolean} keepLoggedIn - 30 days if true, 1 day if false
   * @param {boolean} rememberEmail - Save email for next login
   */
  async login(email, password, keepLoggedIn = true, rememberEmail = false) {
    authLogger.log('Login attempt for:', email, 'keepLoggedIn:', keepLoggedIn);
    
    const response = await apiService.post('/auth/login', {
      email,
      password,
      keep_logged_in: keepLoggedIn,
    }, { includeAuth: false }); // Don't send existing token on login
    
    if (!response.ok) {
      throw new Error(response.error || 'Login failed');
    }
    
    // Store token from response
    if (response.data.session_token) {
      apiService.setToken(response.data.session_token);
      authLogger.log('Token stored from login response');
    } else {
      authLogger.error('No session_token in login response!');
    }
    
    // Handle remember email
    if (rememberEmail) {
      apiService.setRememberedEmail(email);
    } else {
      apiService.setRememberedEmail(null);
    }
    
    this.user = response.data;
    this.notifyListeners();
    this.dispatchEvent(AUTH_EVENTS.LOGIN, { user: this.user });
    
    return this.user;
  }
  
  /**
   * Logout current user
   */
  async logout() {
    // Clear local state immediately for responsive UI
    const hadUser = !!this.user;
    this.user = null;
    apiService.setToken(null);
    this.notifyListeners();
    
    if (hadUser) {
      this.dispatchEvent(AUTH_EVENTS.LOGOUT);
    }
    
    // Try to clear server session (non-blocking)
    try {
      await apiService.post('/auth/logout');
    } catch (err) {
      authLogger.warn('Server logout error (non-blocking):', err);
    }
    
    return true;
  }
  
  /**
   * Refresh user data from server
   * Call after code redemption, profile changes, etc.
   */
  async refreshUser() {
    if (!apiService.hasToken()) {
      authLogger.log('No token for refresh');
      return null;
    }
    
    const response = await apiService.get('/auth/me');
    
    if (response.ok) {
      this.user = response.data;
      authLogger.log('User data refreshed:', this.user.name);
      this.notifyListeners();
      this.dispatchEvent(AUTH_EVENTS.USER_UPDATED, { user: this.user });
      return this.user;
    }
    
    authLogger.error('Failed to refresh user:', response.error);
    return null;
  }
  
  /**
   * Apply pro code
   */
  async applyProCode(code) {
    const response = await apiService.post('/auth/pro-code', { code });
    
    if (!response.ok) {
      throw new Error(response.error || 'Failed to apply code');
    }
    
    // Update local user state
    if (this.user) {
      this.user = {
        ...this.user,
        pro_code: code,
        max_profiles: response.data.max_profiles,
      };
      this.notifyListeners();
      this.dispatchEvent(AUTH_EVENTS.USER_UPDATED, { user: this.user });
    }
    
    return response.data;
  }
  
  /**
   * Change password
   */
  async changePassword(currentPassword, newPassword) {
    const response = await apiService.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    
    if (!response.ok) {
      throw new Error(response.error || 'Failed to change password');
    }
    
    return response.data;
  }
  
  /**
   * Update alert preferences
   */
  async updateAlertPreferences(preferences) {
    const response = await apiService.put('/auth/alerts', preferences);
    
    if (!response.ok) {
      throw new Error(response.error || 'Failed to update preferences');
    }
    
    // Update local user state
    if (this.user) {
      this.user = {
        ...this.user,
        phone: preferences.phone ?? this.user.phone,
        text_alerts_enabled: preferences.text_alerts_enabled ?? this.user.text_alerts_enabled,
        email_alerts_enabled: preferences.email_alerts_enabled ?? this.user.email_alerts_enabled,
      };
      this.notifyListeners();
    }
    
    return response.data;
  }
  
  // Getters
  get isAuthenticated() {
    return !!this.user;
  }
  
  get isLoading() {
    return this.loading;
  }
  
  get currentUser() {
    return this.user;
  }
  
  get natalProfile() {
    return this.user?.natal_profile || null;
  }
  
  get maxProfiles() {
    return this.user?.max_profiles || 3;
  }
  
  get credits() {
    return this.user?.credits || {
      extra_profiles: 0,
      extra_star_spaces: 0,
      extra_preset_tokens: 0,
    };
  }
  
  get rememberedEmail() {
    return apiService.getRememberedEmail();
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
